/**
 * `Settings ▸ Item terminology` (spec M1-B1 §7.4).
 *
 * Free text, with the observed nouns as suggestions. The value is stored **verbatim**,
 * trailing whitespace included — `"Activity "` exists in this account `[fl]` and B5 asserts
 * it round-trips — so the input is never trimmed on the way out. An empty value is stored as
 * `NULL` and the UI falls back to `Item` (E11).
 */
import { useOverlayDismiss } from '@elizmonday/ui';
import { useState } from 'react';
import { ITEM_TERMINOLOGY_PRESETS, itemTermOf, pluralise, useBoard, useBoardMutations } from './hooks';

export function ItemTerminologyDialog({ boardId, onClose }: { boardId: string; onClose(): void }) {
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });

  const board = useBoard(boardId);
  /*
   * The dialog seeds its fields **once**, from the loaded board. Rendering it while the query
   * is still in flight seeded them from `''` and a save then cleared the terminology — the
   * menu can be opened before `shell.board` resolves, which is exactly what the e2e run did.
   */
  if (!board.data) return null;
  return <Form boardId={boardId} current={board.data.itemTerminology ?? ''} onClose={onClose} />;
}

function Form({
  boardId,
  current,
  onClose,
}: {
  boardId: string;
  current: string;
  onClose(): void;
}) {
  const mutations = useBoardMutations();
  const [singular, setSingular] = useState(current);
  const [plural, setPlural] = useState(current === '' ? '' : pluralise(current));

  const preview = itemTermOf(singular === '' ? null : singular, plural === '' ? null : plural);

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Item terminology"
        data-testid="terminology-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          /*
           * `await` before closing, deliberately: `mutate()` + an immediate unmount left the
           * POST in flight, and a reload a moment later aborted it — the dialog said "saved"
           * and nothing was. Every dialog here closes only once the server has answered.
           */
          void (async () => {
            await mutations.update.mutateAsync({
              id: boardId,
              attribute: 'itemTerminology',
              // Verbatim: never `.trim()`. An empty string is `NULL` on the server.
              value: singular === '' ? null : singular,
              plural: plural === '' ? null : plural,
            });
            onClose();
          })();
        }}
      >
        <h2 className="ez-board-dialog__title">Item terminology</h2>
        <p className="ez-board-dialog__hint">
          What one row on this board is called. Every button, counter and menu on the board uses it.
        </p>

        <label className="ez-board-dialog__field">
          Singular
          <input
            data-testid="terminology-singular"
            value={singular}
            maxLength={64}
            placeholder="Item"
            onChange={(event) => setSingular(event.target.value)}
          />
        </label>
        <label className="ez-board-dialog__field">
          Plural
          <input
            data-testid="terminology-plural"
            value={plural}
            maxLength={64}
            placeholder={preview.plural}
            onChange={(event) => setPlural(event.target.value)}
          />
        </label>

        <div className="ez-board-dialog__presets">
          {ITEM_TERMINOLOGY_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className="ez-board-dialog__preset"
              onClick={() => {
                setSingular(preset);
                setPlural(pluralise(preset));
              }}
            >
              {preset}
            </button>
          ))}
        </div>

        <p className="ez-board-dialog__preview" data-testid="terminology-preview">
          {`New ${preview.singular} · 12 ${preview.plural}`}
        </p>

        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="ez-board-dialog__save"
            data-testid="terminology-save"
            disabled={mutations.update.isPending}
          >
            {mutations.update.isPending ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </form>
    </div>
  );
}
